import { useEffect, useMemo, useRef, useState } from 'react';
import { aws, setActor } from '../lib/awsdb';
import { storage } from '../lib/awsStorage';
import { authWrite } from '../lib/authWrite';
import { ensureContact } from '../lib/contacts';
import { EXPENSE_CATEGORIES } from '../lib/expenseCategories';
import { formatMoney, formatBytes } from '../lib/money';
import { daysOverdue } from '../lib/aging';
import { logActivity, formatActivityLine, formatActivityWhen } from '../lib/activityLog';
import { fileToBase64 } from '../lib/fileEncoding';
import FilePreviewModal from './FilePreviewModal';
import ActivityDetailPopup, { parseActivityDetail, activityRefLabel } from './ActivityDetailPopup';
import './drawer.css';

const ACCEPTED_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'application/rtf', 'text/rtf'];
// Some browsers report file.type as '' for RTF files. Fall back to
// the extension so picking an RTF still works.
function isAcceptedInvoiceFile(file) {
  if (!file) return false;
  if (ACCEPTED_MIME.includes(file.type)) return true;
  return /\.rtf$/i.test(file.name || '');
}
const ATTACH_BUCKET = 'po-attachments';

const EMPTY_FORM = {
  po_id: '', invoice_number: '',
  packing_slip_number: '', tr_number: '',
  invoice_date: '', due_date: '',
  subtotal: '', tax: '', total: '', currency: 'AUD', status: 'draft',
  dispute_reason: '', notes: '', expense_category: '', expense_purpose: ''
};

export default function InvoiceDrawer({ session, invoice, prefillPoId, defaultTab, pos, suppliers = [], catalog = [], onClose, onSaved }) {
  const isEdit = !!invoice?.id;
  const [tab, setTab] = useState(defaultTab || (isEdit ? 'activity' : 'invoice'));
  const [form, setForm] = useState(() => {
    if (invoice) return fromInvoice(invoice);
    if (prefillPoId) return { ...EMPTY_FORM, po_id: prefillPoId };
    return EMPTY_FORM;
  });
  const [lines, setLines] = useState([]);
  const [poLines, setPoLines] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attachError, setAttachError] = useState('');
  const [dragover, setDragover] = useState(false);
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [activityDetail, setActivityDetail] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);

  // Extract state
  const [extracting, setExtracting] = useState(false);
  const [autoFilled, setAutoFilled] = useState({});
  const [pendingInvFile, setPendingInvFile] = useState(null);
  const [extractError, setExtractError] = useState('');
  const extractInvRef = useRef(null);

  // New PO inline
  const [newPoOpen, setNewPoOpen] = useState(false);
  const [newPoSupplier, setNewPoSupplier] = useState('');
  const [newPoNumber, setNewPoNumber] = useState('');
  const [newPoError, setNewPoError] = useState('');

  const selectedPo = pos.find(p => p.id === form.po_id) || null;
  const poLocked = !!prefillPoId && !isEdit;

  function clearAutoFill(key) { setAutoFilled(af => { const n = { ...af }; delete n[key]; return n; }); }
  function setFieldAuto(k, v) { setForm(f => ({ ...f, [k]: v })); setAutoFilled(af => ({ ...af, [k]: true })); }
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Data loading ──
  useEffect(() => {
    if (!form.po_id) { setPoLines([]); return; }
    let cancelled = false;
    aws.from('po_line_items').select('id, sku, description, qty_ordered, unit_cost')
      .eq('po_id', form.po_id).order('sku', { ascending: true })
      .then(({ data }) => { if (!cancelled) setPoLines(data || []); });
    return () => { cancelled = true; };
  }, [form.po_id]);

  useEffect(() => {
    if (!isEdit) return;
    Promise.all([
      aws.from('po_invoice_line_items').select('*').eq('invoice_id', invoice.id).order('created_at', { ascending: true }),
      aws.from('po_invoice_payments').select('*').eq('invoice_id', invoice.id).order('paid_at', { ascending: false }),
      aws.from('po_invoice_attachments').select('*').eq('invoice_id', invoice.id).order('uploaded_at', { ascending: false }),
      aws.from('invoice_activity_log').select('*').eq('invoice_id', invoice.id).order('created_at', { ascending: false })
    ]).then(([linesRes, paysRes, attsRes, actRes]) => {
      setLines((linesRes.data || []).map(l => ({
        id: l.id, po_line_item_id: l.po_line_item_id, catalog_id: l.catalog_id || null, sku: l.sku || '',
        description: l.description || '', qty_invoiced: l.qty_invoiced, unit_price: l.unit_price
      })));
      setPayments(paysRes.data || []);
      setAttachments(attsRes.data || []);
      setActivity(actRes.data || []);
    });
  }, [invoice, isEdit]);

  async function refreshActivity(invoiceId) {
    const { data } = await aws.from('invoice_activity_log').select('*').eq('invoice_id', invoiceId).order('created_at', { ascending: false });
    setActivity(data || []);
  }

  async function postComment() {
    if (!commentText.trim() || !invoice?.id) return;
    setPosting(true);
    await logActivity(invoice.id, session, {
      action_type: 'comment',
      notes: commentText.trim()
    });
    setCommentText('');
    setPosting(false);
    refreshActivity(invoice.id);
  }

  // ── Computed values ──
  const paidTotal = useMemo(() => payments.reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);
  const linesTotal = useMemo(() => lines.reduce((s, l) => s + (Number(l.qty_invoiced || 0) * Number(l.unit_price || 0)), 0), [lines]);
  const hasLines = lines.length > 0;
  const effectiveSubtotal = hasLines ? linesTotal : Number(form.subtotal || 0);
  const effectiveTotal = hasLines ? effectiveSubtotal + Number(form.tax || 0) : Number(form.total || 0);
  const outstanding = effectiveTotal - paidTotal;
  const daysOver = form.due_date ? daysOverdue(form.due_date) : null;
  const daysOverLabel = daysOver == null ? '—' : daysOver > 0 ? `+${daysOver}` : daysOver === 0 ? 'due today' : String(daysOver);
  const autoPaid = effectiveTotal > 0 && outstanding <= 0.01;
  const displayStatus = autoPaid ? 'paid' : (form.status || 'draft');

  const poOptions = useMemo(() => {
    const bySupplier = {};
    pos.forEach(po => { (bySupplier[po.supplier_name || 'Unknown'] = bySupplier[po.supplier_name || 'Unknown'] || []).push(po); });
    return bySupplier;
  }, [pos]);

  // ── Extract invoice ──
  async function extractInvoice(file) {
    setExtracting(true);
    setExtractError('');
    try {
      const base64 = await fileToBase64(file);
      // Some browsers report empty file.type for RTF; fall back to
      // the extension so the Lambda picks the right code path.
      let mime = file.type;
      if (!mime && /\.rtf$/i.test(file.name || '')) mime = 'application/rtf';
      const res = await fetch(import.meta.env.VITE_AWS_EXTRACT_INVOICE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: base64, mime_type: mime, userPin: session.code })
      });
      if (!res.ok) {
        let msg = `Extract failed (HTTP ${res.status})`;
        try { const e = await res.json(); if (e?.error) msg = e.error; } catch {}
        setExtractError(`${file.name}: ${msg}`);
        setExtracting(false);
        return;
      }
      const d = await res.json();
      const filled = [];
      if (d.invoice_number) { setFieldAuto('invoice_number', String(d.invoice_number)); filled.push('invoice_number'); }
      if (d.invoice_date) { setFieldAuto('invoice_date', d.invoice_date); filled.push('invoice_date'); }
      if (d.due_date) { setFieldAuto('due_date', d.due_date); filled.push('due_date'); }
      if (d.subtotal != null) { setFieldAuto('subtotal', String(d.subtotal)); filled.push('subtotal'); }
      if (d.tax != null) { setFieldAuto('tax', String(d.tax)); filled.push('tax'); }
      if (d.total != null) { setFieldAuto('total', String(d.total)); filled.push('total'); }
      // notes field removed — use comments instead
      if (Array.isArray(d.line_items) && d.line_items.length > 0) {
        setLines(d.line_items.map(li => ({
          _local: Math.random().toString(36).slice(2, 8), po_line_item_id: null, sku: '',
          description: li.description || '', qty_invoiced: li.qty != null ? String(li.qty) : '',
          unit_price: li.unit_cost != null ? String(li.unit_cost) : ''
        })));
        filled.push('line_items');
      }
      setPendingInvFile(file);
      // 200 with all-null fields means Claude saw nothing it recognised
      // as an invoice — surface that rather than silently looking broken.
      if (filled.length === 0) {
        setExtractError(`${file.name}: extracted but no invoice fields found. Fill manually.`);
      }
    } catch (err) {
      setExtractError(`${file.name}: ${err?.message || 'extract failed'}`);
    }
    setExtracting(false);
  }

  // ── Handlers ──
  async function createNewPo() {
    setNewPoError('');
    const name = newPoSupplier.trim(), num = newPoNumber.trim();
    if (!name || !num) return setNewPoError('Supplier and PO number both required.');
    if (pos.some(p => p.po_number === num)) return setNewPoError(`PO #${num} already exists.`);
    try {
      // status omitted → DB default 'draft'. Don't pass 'open' here:
      // there is NO 'open' status on purchase_orders (CHECK constraint
      // allows draft|approved|sent|partial|received|closed|cancelled).
      // Same bug class as the 2026-05-07 reopen-button 500.
      const rows = await authWrite(session, 'purchase_orders', 'insert', { po_number: num, supplier_name: name, currency: 'AUD' });
      setField('po_id', rows[0].id);
    } catch (err) { return setNewPoError(err.message); }
    setNewPoOpen(false); setNewPoSupplier(''); setNewPoNumber('');
  }

  async function save() {
    if (!form.po_id) { alert('Select a Purchase Order.'); return; }
    if (!form.invoice_number) { alert('Invoice Number is required.'); return; }
    setSaving(true);

    const po = pos.find(p => p.id === form.po_id);
    const invoiceData = {
      po_id: form.po_id,
      supplier_id: po?.supplier_id || null,
      supplier_name: po?.supplier_name || null,
      po_number_text: po?.po_number || null,
      invoice_number: form.invoice_number,
      packing_slip_number: form.packing_slip_number?.trim() || null,
      tr_number: form.tr_number?.trim() || null,
      invoice_date: form.invoice_date || null,
      due_date: form.due_date || null,
      subtotal: String(effectiveSubtotal),
      tax: String(parseFloat(form.tax) || 0),
      total: String(effectiveTotal),
      amount_due: String(effectiveTotal),
      currency: form.currency,
      status: form.status,
      dispute_reason: form.dispute_reason || null,
      notes: form.notes || null
    };

    const lineData = lines
      .filter(l => l.description || l.sku || l.catalog_id || l.qty_invoiced)
      .map(l => ({
        po_line_item_id: l.po_line_item_id || null,
        catalog_id: l.catalog_id || null,
        sku: l.sku || null,
        description: l.description || null,
        qty_invoiced: Number(l.qty_invoiced) || 0,
        unit_price: Number(l.unit_price) || 0
      }));

    const { data: result, error } = await aws.rpc('save_invoice', {
      p_invoice_id: invoice?.id || null,
      p_data: invoiceData,
      p_lines: lineData,
      p_actor_code: session.code,
      p_actor_name: session.name,
      p_expected_updated_at: invoice?.updated_at || null
    });

    if (error) {
      setSaving(false);
      if (error.message?.includes('Conflict')) {
        alert('This invoice was modified by someone else while you were editing. Please close and reopen it to get the latest version.');
      } else {
        alert('Save failed: ' + error.message);
      }
      return;
    }

    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    const invoiceId = parsed?.id || invoice?.id;

    // File attachment stays separate (Supabase Storage can't run inside Postgres)
    if (pendingInvFile && invoiceId) {
      try {
        const safeName = pendingInvFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const key = `invoice/${invoiceId}/${Date.now()}-${safeName}`;
        const { error: upErr, data: upData } = await storage.from(ATTACH_BUCKET).upload(key, pendingInvFile, { contentType: pendingInvFile.type, upsert: false });
        if (upErr) {
          console.error('[auto-attach] upload failed:', upErr.message);
        } else {
          const storedPath = upData?.path || key;
          try {
            await authWrite(session, 'po_invoice_attachments', 'insert', {
              invoice_id: invoiceId, storage_path: storedPath, filename: pendingInvFile.name,
              mime_type: pendingInvFile.type, size_bytes: pendingInvFile.size, uploaded_by: session.code,
            });
            await logActivity(invoiceId, session, { action_type: 'attachment_added', after_value: pendingInvFile.name, notes: `proof:${ATTACH_BUCKET}:${storedPath}` });
          } catch (rowErr) {
            console.error('[auto-attach] row insert failed:', rowErr.message);
          }
        }
      } catch (e) {
        console.error('[auto-attach] exception:', e);
      }
    }

    ensureContact(pos.find(p => p.id === form.po_id)?.supplier_name);
    setSaving(false);
    onSaved?.();
    onClose();
  }

  const addLine = () => setLines(ls => [...ls, { _local: Math.random().toString(36).slice(2, 8), po_line_item_id: null, catalog_id: null, sku: '', description: '', qty_invoiced: '', unit_price: '' }]);
  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  async function uploadFiles(files) {
    setAttachError('');
    if (!invoice?.id) { setAttachError('Save the invoice first.'); return; }
    await setActor(session);
    for (const file of files) {
      if (!isAcceptedInvoiceFile(file)) { setAttachError(`${file.name}: only PDF, PNG, JPG, RTF allowed.`); continue; }
      const key = `invoice/${invoice.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: upErr, data: upData } = await storage.from(ATTACH_BUCKET).upload(key, file, { contentType: file.type, upsert: false });
      if (upErr) { setAttachError(`${file.name}: ${upErr.message}`); continue; }
      const storedPath = upData?.path || key;
      try {
        await authWrite(session, 'po_invoice_attachments', 'insert', {
          invoice_id: invoice.id, storage_path: storedPath, filename: file.name,
          mime_type: file.type, size_bytes: file.size, uploaded_by: session.code,
        });
        await logActivity(invoice.id, session, { action_type: 'attachment_added', after_value: file.name, notes: `proof:${ATTACH_BUCKET}:${storedPath}` });
      } catch (rowErr) {
        await storage.from(ATTACH_BUCKET).remove([storedPath]);
        setAttachError(`${file.name}: ${rowErr.message}`);
      }
    }
    const { data } = await aws.from('po_invoice_attachments').select('*').eq('invoice_id', invoice.id).order('uploaded_at', { ascending: false });
    setAttachments(data || []);
    refreshActivity(invoice.id);
  }

  async function previewFile(storagePath, filename, bucket = ATTACH_BUCKET) {
    const { data, error } = await storage.from(bucket).createSignedUrl(storagePath, 3600);
    if (error || !data?.signedUrl) { alert('Could not open file'); return; }
    setPreview({ url: data.signedUrl, filename });
  }

  async function removeAttachment(id, storagePath) {
    const target = attachments.find(a => a.id === id);
    if (!confirm('Delete this attachment?')) return;
    await setActor(session);
    await authWrite(session, 'po_invoice_attachments', 'delete', null, { column: 'id', value: id });
    await storage.from(ATTACH_BUCKET).remove([storagePath]);
    if (target) await logActivity(invoice.id, session, { action_type: 'attachment_deleted', before_value: target.filename });
    setAttachments(atts => atts.filter(a => a.id !== id));
    refreshActivity(invoice.id);
  }

  // ── Render ──
  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div className="drawer-title">{isEdit ? (invoice.invoice_number || 'Edit Invoice') : 'Log Invoice'}</div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Tabs */}
        {isEdit && (
          <div className="po-drawer-tabs">
            <button className={`po-drawer-tab ${tab === 'invoice' ? 'active' : ''}`} onClick={() => setTab('invoice')}>Invoice</button>
            <button className={`po-drawer-tab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>Activity</button>
          </div>
        )}

        <div className="drawer-content">
          {/* ── Invoice tab ── */}
          {(tab === 'invoice' || !isEdit) && (<>
            {/* Extract drop zone — new only */}
            {!isEdit && (
              <>
                <div className={`po-extract-zone ${extracting ? 'extracting' : ''}`}
                  onClick={() => !extracting && extractInvRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f && isAcceptedInvoiceFile(f)) extractInvoice(f); }}>
                  {extracting ? 'Reading invoice...' : pendingInvFile ? <span>Extracted from <strong>{pendingInvFile.name}</strong></span> : <span>Drop an invoice to auto-fill <span className="muted-xs">PDF, PNG, JPG, RTF</span></span>}
                </div>
                {pendingInvFile && !extracting && (
                  <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
                    onClick={() => setPreview({ url: URL.createObjectURL(pendingInvFile), filename: pendingInvFile.name })}>
                    Preview uploaded file
                  </button>
                )}
                <input ref={extractInvRef} type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" hidden onChange={e => { const f = e.target.files?.[0]; if (f) extractInvoice(f); e.target.value = ''; }} />
                {extractError && <div className="inline-error" style={{ marginTop: 6 }}>{extractError}</div>}
              </>
            )}

            {/* Header fields */}
            <div className="form-group">
              <label className="form-label">Purchase Order<span className="required-star">*</span></label>
              {poLocked ? (
                <>
                  <div className="readonly-value">PO #{selectedPo?.po_number || '—'}</div>
                  <div className="form-hint">Supplier: {selectedPo?.supplier_name || '—'} · Currency: {selectedPo?.currency || 'AUD'}</div>
                </>
              ) : (
                <>
                  <select className="form-select" value={form.po_id} onChange={e => setField('po_id', e.target.value)}>
                    <option value="">Select PO...</option>
                    {Object.keys(poOptions).sort().map(sn => (
                      <optgroup key={sn} label={sn}>
                        {poOptions[sn].map(po => (<option key={po.id} value={po.id}>PO #{po.po_number}</option>))}
                      </optgroup>
                    ))}
                  </select>
                  {selectedPo && <div className="form-hint">Supplier: {selectedPo.supplier_name || '—'} · Currency: {selectedPo.currency || 'AUD'}</div>}
                </>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Invoice Number<span className="required-star">*</span></label>
              <input className={`form-input ${autoFilled.invoice_number ? 'auto-filled' : ''}`} value={form.invoice_number} onChange={e => { setField('invoice_number', e.target.value); clearAutoFill('invoice_number'); }} placeholder="e.g. INV608397" />
            </div>

            <div className="row-2">
              <div className="form-group">
                <label className="form-label">Packing Slip #</label>
                <input className="form-input" value={form.packing_slip_number} onChange={e => setField('packing_slip_number', e.target.value)} placeholder="Optional · from packing slip" />
              </div>
              <div className="form-group">
                <label className="form-label">Skutopia TR #</label>
                <input className="form-input" value={form.tr_number} onChange={e => setField('tr_number', e.target.value)} placeholder="Optional · Skutopia transfer ref" />
              </div>
            </div>

            <div className="row-2">
              <div className="form-group">
                <label className="form-label">Invoice Date</label>
                <input type="date" className="form-input" value={form.invoice_date || ''} onChange={e => setField('invoice_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input type="date" className="form-input" value={form.due_date || ''} onChange={e => setField('due_date', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-select" value={form.currency} onChange={e => setField('currency', e.target.value)}>
                <option value="AUD">AUD</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
              </select>
            </div>

            <div className="row-2">
              <div className="form-group">
                <label className="form-label">Expense Category</label>
                <select className="form-select" value={form.expense_category} onChange={e => setField('expense_category', e.target.value)}>
                  <option value="">Select...</option>
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Purpose</label>
                <input className="form-input" value={form.expense_purpose} onChange={e => setField('expense_purpose', e.target.value)} placeholder="Brief accounting justification" />
              </div>
            </div>

            {/* Money strip */}
            <div className="inv-money-strip">
              <div className="inv-money-item">
                <div className="inv-money-label">Total</div>
                <div className="inv-money-value">{formatMoney(effectiveTotal)}</div>
              </div>
              <div className="inv-money-item">
                <div className="inv-money-label">Paid</div>
                <div className="inv-money-value" style={{ color: '#036e43' }}>{formatMoney(paidTotal)}</div>
              </div>
              <div className="inv-money-item">
                <div className="inv-money-label">Outstanding</div>
                <div className="inv-money-value" style={{ color: outstanding > 0 && daysOver > 0 ? 'var(--text-error)' : 'var(--color-primary)' }}>{formatMoney(Math.max(0, outstanding))}</div>
              </div>
            </div>

            {/* Status + Actions row */}
            <div className="inv-status-row">
              <span className={`status-pill ${displayStatus}`}>{displayStatus.replace(/_/g, ' ')}</span>
              {isEdit && displayStatus !== 'paid' && (
                <div className="inv-status-actions">
                  {displayStatus === 'approved' && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => { /* handled by PoTracker actions */ onClose(); }}>+ Add Payment</button>
                  )}
                </div>
              )}
            </div>

            {/* Line items */}
            <div className="subsection">
              <div className="subsection-label">Line Items {lines.length > 0 && <span className="muted-xs">· {lines.length}</span>}</div>
              <div className="subsection-body">
                {lines.length === 0 ? (
                  <div className="muted-small">No lines yet.</div>
                ) : (
                  <div className="line-items-table">
                    <div className="line-item-head">
                      <div>Description / SKU</div>
                      <div>Catalogue SKU</div>
                      <div style={{ textAlign: 'right' }}>Qty</div>
                      <div style={{ textAlign: 'right' }}>Unit $</div>
                      <div style={{ textAlign: 'right' }}>Line total</div>
                      <div />
                    </div>
                    {lines.map((line, idx) => {
                      const qty = Number(line.qty_invoiced || 0);
                      const price = Number(line.unit_price || 0);
                      const lineTotal = qty * price;
                      return (
                        <div key={line.id || line._local}>
                          <div className="line-item-row">
                            <input type="text" value={line.description || ''} onChange={e => updateLine(idx, { description: e.target.value })} placeholder="Description or SKU" />
                            <select value={line.catalog_id || ''} onChange={e => {
                              const catId = e.target.value || null;
                              const cat = catalog.find(c => c.id === catId);
                              const patch = { catalog_id: catId };
                              if (cat) {
                                if (!line.description) patch.description = cat.sku_name;
                                if (!line.sku) patch.sku = cat.sku_code || '';
                                if (line.unit_price === '' || line.unit_price == null) patch.unit_price = Number(cat.unit_cost) || 0;
                              }
                              updateLine(idx, patch);
                            }} style={{ borderColor: !line.catalog_id && line.description ? '#f59e0b' : undefined }}>
                              <option value="">{!line.catalog_id && line.description ? '⚠ unlinked' : '— unlinked —'}</option>
                              {catalog.filter(c => c.status === 'active').map(c => (<option key={c.id} value={c.id}>{c.sku_code ? `${c.sku_code} · ` : ''}{c.sku_name}</option>))}
                            </select>
                            <input type="number" step="1" value={line.qty_invoiced ?? ''} onChange={e => updateLine(idx, { qty_invoiced: e.target.value })} style={{ textAlign: 'right' }} />
                            <input type="number" step="0.01" value={line.unit_price ?? ''} onChange={e => updateLine(idx, { unit_price: e.target.value })} style={{ textAlign: 'right' }} />
                            <div className="line-total-cell">{formatMoney(lineTotal)}</div>
                            <button type="button" className="icon-del" onClick={() => removeLine(idx)} title="Remove">×</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={addLine}>+ Add line</button>
                {hasLines && <div className="muted-small" style={{ marginTop: 8 }}>Lines total: {formatMoney(linesTotal)}</div>}
              </div>
            </div>

            {/* Totals row */}
            <div className="row-3">
              <div className="form-group">
                <label className="form-label">Subtotal{hasLines ? <span className="field-hint">from lines</span> : null}</label>
                {hasLines ? <div className="readonly-value">{formatMoney(effectiveSubtotal)}</div>
                  : <input type="number" className="form-input" step="0.01" value={form.subtotal} onChange={e => setField('subtotal', e.target.value)} placeholder="0.00" />}
              </div>
              <div className="form-group">
                <label className="form-label">Tax</label>
                <input type="number" className="form-input" step="0.01" value={form.tax} onChange={e => setField('tax', e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Total{hasLines ? <span className="field-hint">subtotal + tax</span> : null}</label>
                {hasLines ? <div className="readonly-value">{formatMoney(effectiveTotal)}</div>
                  : <input type="number" className="form-input" step="0.01" value={form.total} onChange={e => setField('total', e.target.value)} placeholder="0.00" />}
              </div>
            </div>

            {/* Attachments */}
            <div className="form-group">
              <div
                className={`attach-dropzone compact ${dragover ? 'dragover' : ''} ${!isEdit ? 'disabled' : ''}`}
                onClick={() => isEdit && fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); if (isEdit) setDragover(true); }}
                onDragLeave={() => setDragover(false)}
                onDrop={e => { e.preventDefault(); setDragover(false); if (!isEdit) return; uploadFiles([...(e.dataTransfer?.files || [])]); }}>
                {isEdit ? <span>Drop files or click to upload <span className="muted-xs">PDF, PNG, JPG, RTF</span></span> : 'Save first to attach files'}
              </div>
              <input ref={fileInputRef} type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" multiple hidden onChange={e => { uploadFiles([...(e.target.files || [])]); e.target.value = ''; }} />
              {attachments.length > 0 && (
                <div className="inv-file-chips">
                  {attachments.map(a => (
                    <div key={a.id} className="inv-file-chip">
                      <span className="inv-file-chip-name" title={a.filename}>{a.filename}</span>
                      <span className="inv-file-chip-size">{formatBytes(a.size_bytes)}</span>
                      <button type="button" className="file-eye-btn" onClick={() => previewFile(a.storage_path, a.filename)} title="Preview">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
                      </button>
                      <button type="button" className="icon-del" onClick={() => removeAttachment(a.id, a.storage_path)} title="Delete">×</button>
                    </div>
                  ))}
                </div>
              )}
              {attachError && <div className="inline-error">{attachError}</div>}
            </div>

          </>)}

          {/* ── Activity tab ── */}
          {tab === 'activity' && isEdit && (
            <div className="po-comments-tab">
              <div className="po-comment-input">
                <textarea
                  className="form-textarea"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
                />
                <button className="btn btn-primary btn-sm" disabled={posting || !commentText.trim()} onClick={postComment}>
                  {posting ? 'Posting...' : 'Post'}
                </button>
              </div>
              <div className="po-unified-feed">
                {activity.length === 0 && <div className="po-card-activity-empty">No activity or comments yet</div>}
                {activity.map(entry => {
                  const isComment = entry.action_type === 'comment';
                  const detail = parseActivityDetail(entry.notes);
                  const ref = activityRefLabel(entry);
                  const proofMatch = !detail && !isComment && entry.notes?.match(/^proof:([^:]+):(.+)$/);
                  const isClickable = !!detail;

                  if (isComment) {
                    return (
                      <div key={entry.id} className="po-feed-comment">
                        <div className="po-feed-comment-header">
                          <span className="po-feed-comment-author">{entry.actor_name || 'Someone'}</span>
                          <span className="po-feed-comment-when">{formatActivityWhen(entry.created_at)}</span>
                        </div>
                        <div className="po-feed-comment-body">{entry.notes}</div>
                      </div>
                    );
                  }

                  return (
                    <div key={entry.id} className={`po-feed-activity ${isClickable ? 'activity-clickable' : ''}`} onClick={isClickable ? () => setActivityDetail(entry) : undefined}>
                      <span className="po-feed-activity-who">{entry.actor_name || 'System'}</span>
                      <span className="po-feed-sep">·</span>
                      <span className="po-feed-activity-what">
                        {formatActivityLine(entry)}
                        {ref && <span className="activity-ref-badge">{ref}</span>}
                        {proofMatch && (
                          <button type="button" className="file-eye-btn" style={{ marginLeft: 6 }} onClick={e => { e.stopPropagation(); previewFile(proofMatch[2], entry.after_value || 'proof', proofMatch[1]); }} title="View file">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
                          </button>
                        )}
                      </span>
                      <span className="po-feed-sep">·</span>
                      <span className="po-feed-activity-when">{formatActivityWhen(entry.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer — Invoice tab only */}
        {(tab === 'invoice' || !isEdit) && (
          <div className="drawer-footer">
            <button className="btn btn-secondary btn-full" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-full" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </aside>
      {preview && <FilePreviewModal url={preview.url} filename={preview.filename} onClose={() => setPreview(null)} />}
      {activityDetail && <ActivityDetailPopup entry={activityDetail} onClose={() => setActivityDetail(null)} />}
    </>
  );
}


function fromInvoice(inv) {
  return {
    po_id: inv.po_id || '', invoice_number: inv.invoice_number || '',
    packing_slip_number: inv.packing_slip_number || '', tr_number: inv.tr_number || '',
    invoice_date: inv.invoice_date || '', due_date: inv.due_date || '',
    subtotal: inv.subtotal ?? 0, tax: inv.tax ?? 0, total: inv.total ?? 0,
    currency: inv.currency || 'AUD', status: inv.status || 'draft',
    dispute_reason: inv.dispute_reason || '', notes: inv.notes || '',
    expense_category: inv.expense_category || '', expense_purpose: inv.expense_purpose || ''
  };
}
